import { jsx } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { useClickOutside } from '@zapier/design-system';
import { SIDEBAR_ID } from '../constants';
import { BREAKPOINT_UNIVERSAL_SIDEBAR_SHOW_COLLAPSED, BREAKPOINT_UNIVERSAL_SIDEBAR_SHOW_OPEN, UNIVERSAL_NAV_ZINDEX, UNIVERSAL_SIDEBAR_WIDTH, UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH, } from '../../../../universal-nav/constants';
import { CreateDropdown } from './CreateDropdown';
import { Divider } from '../Divider';
import Footer from './Footer';
import InAppLinks from './InAppLinks';
import { useQuerySidebar } from './useQuerySidebar';
import { useUniversalNavContext } from '../../../../universal-nav/hooks';
import useUnscrollableBody from '../../../../universal-nav/hooks/useUnscrollableBody';
import { boxSizing } from '../../../../utils/style-mixins/reset';
const Styles = labelStyles('InAppSidebar', {
    root: ({ isExpanded, isFloating, isCreateMenuOpen, isSidebarForcedOpen, }) => [
        Object.assign(Object.assign({}, boxSizing), { position: 'relative', containerType: 'inline-size', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--zds-background-weaker, #FFFDF9)', padding: 'var(--zds-space-16, 16px) 0 var(--zds-space-8, 8px)', height: '100%', willChange: 'transform, width', overflowY: isCreateMenuOpen ? 'unset' : 'auto', scrollBehavior: 'smooth', '::-webkit-scrollbar': {
                width: '0px',
                background: 'transparent',
            }, scrollbarWidth: 'none', justifyContent: 'space-between', width: '0px', [`@media (min-width: ${BREAKPOINT_UNIVERSAL_SIDEBAR_SHOW_COLLAPSED}px)`]: {
                width: `${UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH}px`,
            }, [`@media (min-width: ${BREAKPOINT_UNIVERSAL_SIDEBAR_SHOW_OPEN}px)`]: {
                width: `${isSidebarForcedOpen
                    ? UNIVERSAL_SIDEBAR_WIDTH
                    : UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH}px`,
            } }),
        isFloating && {
            boxShadow: 'var(--zds-shadow-surface, 0 1px 18px 0 rgba(20,15,15,0.12), 0 6px 10px 0 rgba(20,15,15,0.14), 0 3px 5px -1px rgba(20,15,15,0.2))',
            [`@media (min-width: ${BREAKPOINT_UNIVERSAL_SIDEBAR_SHOW_OPEN}px)`]: {
                boxShadow: isSidebarForcedOpen && 'none',
            },
        },
        isExpanded &&
            withModifier('expanded', {
                width: UNIVERSAL_SIDEBAR_WIDTH,
            }),
    ],
    rootWrapper: () => [
        Object.assign(Object.assign({}, boxSizing), { alignSelf: 'start', gridArea: 'sidebar', position: 'sticky', height: '100%', top: 0, zIndex: UNIVERSAL_NAV_ZINDEX - 1 }),
    ],
    header: Object.assign(Object.assign({}, boxSizing), { flex: 'none', padding: '0 var(--zds-space-8, 8px)', marginBottom: 'var(--zds-space-12, 12px)' }),
    content: () => [
        Object.assign(Object.assign({}, boxSizing), { flexBasis: 'auto', flexShrink: 0, flexGrow: 0, scrollbarWidth: 'none', '::-webkit-scrollbar': {
                display: 'none',
            } }),
    ],
    footer: Object.assign(Object.assign({}, boxSizing), { flex: 'none', padding: '0 var(--zds-space-8, 8px)', width: '100%', [`@container (max-width: ${UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH}px)`]: {
            display: 'none',
        } }),
});
const InAppSidebar = ({ currentAccountId, rootRef, selectedHref = '', }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { data: sideNavData } = useQuerySidebar();
    const { isCreateMenuOpen, isSidebarForcedOpen, isSidebarOpenedByTopbar, setIsSidebarOpenedByTopbar, setIsCreateMenuOpen, setIsMoreLinksMenuOpen, } = useUniversalNavContext();
    const isActuallyExpanded = isExpanded || isSidebarOpenedByTopbar;
    const isFloating = isExpanded || isSidebarOpenedByTopbar;
    const sidebarRef = useRef(null);
    const [distanceFromTop, setDistanceFromTop] = useState(null);
    useEffect(() => {
        const setDistance = () => {
            if (sidebarRef.current) {
                setDistanceFromTop(sidebarRef.current.getBoundingClientRect().top);
            }
        };
        setDistance();
        const resizeObserver = new ResizeObserver(setDistance);
        if (rootRef === null || rootRef === void 0 ? void 0 : rootRef.current) {
            resizeObserver.observe(rootRef.current);
        }
        document.addEventListener('scroll', setDistance, {
            capture: true,
            passive: true,
        });
        return () => {
            resizeObserver.disconnect();
            document.removeEventListener('scroll', setDistance);
        };
    }, [rootRef]);
    const mouseProps = {
        onClick: (event) => {
            var _a, _b;
            const target = event.target;
            const triggerIsCreateMenuButton = (_a = target.parentElement) === null || _a === void 0 ? void 0 : _a.classList.contains('sidebar-create-menu-button');
            const triggerIsMoreButton = (_b = target.classList) === null || _b === void 0 ? void 0 : _b.contains('MoreButton');
            const trigger = target.closest('a, button, label, [role="button"]');
            if (isSidebarOpenedByTopbar &&
                !!trigger &&
                !triggerIsCreateMenuButton &&
                !triggerIsMoreButton) {
                setIsSidebarOpenedByTopbar(false);
            }
        },
        onMouseEnter: () => {
            if (window.matchMedia('(hover: hover)').matches) {
                setIsExpanded(true);
            }
        },
        onMouseLeave: () => {
            if (window.matchMedia('(hover: hover)').matches) {
                setIsExpanded(false);
                setIsCreateMenuOpen(false);
                setIsMoreLinksMenuOpen(false);
            }
        },
    };
    const [setClickOutsideNode] = useClickOutside({
        onClickOutside: (event) => {
            const target = event.target;
            if (target.closest('[data-testid="universalTopbar"]'))
                return;
            setIsSidebarOpenedByTopbar(false);
        },
    });
    useEffect(() => {
        if (sidebarRef.current)
            setClickOutsideNode(sidebarRef.current);
    }, [sidebarRef]);
    useUnscrollableBody({ shouldAllowScroll: !isSidebarOpenedByTopbar });
    return (jsx("div", { ref: sidebarRef, "data-zds": true, css: Styles.rootWrapper(), style: {
            height: distanceFromTop !== null
                ? `calc(100vh - ${Math.max(0, distanceFromTop)}px)`
                : '',
        } },
        jsx("nav", Object.assign({ "aria-label": "Site Links", css: Styles.root({
                isExpanded: isActuallyExpanded,
                isFloating,
                isCreateMenuOpen,
                isSidebarForcedOpen,
            }), "data-testid": "universalSidebar", id: SIDEBAR_ID }, mouseProps, { "data-zds": true }),
            jsx("div", { "data-zds": true },
                jsx("div", { css: Styles.header, "data-zds": true },
                    jsx(CreateDropdown, { items: sideNavData.createOptions })),
                jsx("div", { css: Styles.content(), "data-zds": true },
                    jsx(InAppLinks, { links: sideNavData.itemGroups, moreLinks: sideNavData.moreLinks, selectedHref: selectedHref }))),
            jsx("div", { css: Styles.footer, role: "region", "data-zds": true },
                jsx(Divider, null),
                jsx(Footer, { currentAccountId: currentAccountId, selectedHref: selectedHref })))));
};
export default InAppSidebar;
//# sourceMappingURL=InAppSidebar.js.map
// @zapier/universal-layout@11.2.3 (b1a6e8603)
